import { Formik } from 'formik';
import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Border, Breakpoints, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import { FormField, FormFieldContainer, Label } from '../../../components/FormElements';
import { ModalFooter } from '../../../components/ModalElements';
import Overlay from '../../../components/Overlay';
import { RichTextEditor } from '../../../components/richTextEditor';
import StepIcons from '../../../components/StepIcons';
import { useUser } from '../../authentication';
import { Content } from '../models/content';
import { CourseStep } from '../models/course';
import { StepType } from '../models/stepType';
import { editStep, handleContentSwitchSubmit, mapApiTypeToStepType } from '../utils/courseStepUtils';
import { ActionButton } from './ActionButton';
import { ContentActionButton } from './ContentActionButton';
import { ContentStep } from './ContentStep';
import { VideoPreview } from './VideoPreview';

const StyledFormFieldContainer = styled(FormFieldContainer)`
  margin-bottom: ${Grid._2};
`;

const EditStepContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;
const OverlayContainer = styled.div`
  width: 100vw;
  padding: ${Grid._6};

  h3 {
    font-size: ${Type.Scale._5};
    font-weight: ${Type.Weight.bold};
    margin-top: 0;
    margin-bottom: ${Grid._4};
    color: ${Color.Gray._90};
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    width: 85vw;
    max-width: ${Breakpoints.screen_md};
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Grid._4};

  i {
    font-size: ${Type.Scale._4};
    color: ${Type.Color.medium};
  }
`;

const CurrentItemCard = styled.div`
  display: flex;
  padding: ${Grid._4};
  margin-bottom: ${Grid._4};
  border: 1px solid ${Color.Gray._10};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.small};
  width: 100%;
  align-items: center;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: ${Grid._2};

  h4,
  p {
    margin: 0;
  }

  h4 {
    font-weight: ${Type.Weight.semibold};
  }

  p {
    font-weight: ${Type.Weight.light};
  }
`;

type EditCourseStepModalProps = {
  courseId: string;
  stepOrder: number;
  handleDismiss: () => void;
  onSuccess?: (step: CourseStep) => void | Promise<void>;
  existingStep: CourseStep;
};

const getValidationSchema = (selectedStepType: StepType) => {
  const baseSchema = {
    name: Yup.string().trim().required('Name is required'),
    description: Yup.string().trim().optional(),
  };

  if (selectedStepType === 'link' || selectedStepType === 'youtube' || selectedStepType === 'vimeo') {
    return Yup.object({
      ...baseSchema,
      url: Yup.string().trim().url('Please enter a valid URL').required('URL is required'),
    });
  }

  return Yup.object(baseSchema);
};

type CurrentStep = 'form' | 'select' | 'preview' | 'upload';

const getInitialValues = (
  selectedStepType: StepType,
  existingStep: CourseStep
): {
  stepType: StepType;
  name: string;
  description: string;
  url?: string;
  selectedContents: Content[];
} => {
  const url = existingStep.linkUrl || existingStep.videoUrl || '';
  return {
    stepType: (selectedStepType || 'text') as StepType,
    name: existingStep.taskName || '',
    description: existingStep.taskDescription || '',
    url,
    selectedContents: [],
  };
};

export const EditCourseStepModal = ({
  courseId,
  stepOrder,
  handleDismiss,
  onSuccess = () => Promise.resolve(),
  existingStep,
}: EditCourseStepModalProps) => {
  const { user } = useUser();
  const selectedStepType: StepType = mapApiTypeToStepType(existingStep._type, existingStep.thirdPartySite) as StepType;
  const [currentStep, setCurrentStep] = useState<CurrentStep>('form');
  const [contentSearchQuery, setContentSearchQuery] = useState<string>('');
  const [selectedContents, setSelectedContents] = useState<Content[]>([]);
  const [previewContent, setPreviewContent] = useState<{ name: string; id: string }>();
  const [error, setError] = useState(false);

  const handlePreviewContent = (content: { name: string; id: string }) => {
    setPreviewContent(content);
    setCurrentStep('preview');
  };

  const isContentStepType =
    selectedStepType === 'assessment' ||
    selectedStepType === 'download' ||
    selectedStepType === 'video' ||
    selectedStepType === 'audio' ||
    selectedStepType === 'documentViewer';

  const handleSubmit = (values: {
    stepType: StepType;
    name: string;
    description: string;
    url?: string;
    selectedContents: Content[];
  }) => {
    if (!user?.userId) {
      return;
    }

    return editStep({
      courseId,
      stepOrder,
      userId: user.userId,
      values,
      existingStep,
    })
      .then((steps: CourseStep | CourseStep[]) => {
        if (Array.isArray(steps)) {
          steps.forEach(step => onSuccess(step));
        } else {
          onSuccess(steps);
        }
        handleDismiss();
      })
      .catch(() => {
        setError(true);
      });
  };

  const handleSwitchContent = () => {
    setCurrentStep('select');
  };

  const handleBack = () => {
    setCurrentStep('form');
    setSelectedContents([]);
    setPreviewContent(undefined);
  };

  const renderFormFields = (
    setFieldValue: (field: string, value: string | Content[]) => void,
    values: {
      stepType: StepType;
      name: string;
      description: string;
      url?: string;
      selectedContents: Content[];
    }
  ) => {
    if (!selectedStepType) {
      return null;
    }

    if ((currentStep === 'select' || currentStep === 'preview' || currentStep === 'upload') && isContentStepType) {
      return (
        <ContentStep
          selectedContents={selectedContents}
          contentSearchQuery={contentSearchQuery}
          setSelectedContents={(contents: Content[]) => {
            setSelectedContents(contents);
            setFieldValue('selectedContents', contents);
          }}
          setContentSearchQuery={setContentSearchQuery}
          stepType={selectedStepType}
          currentStep={currentStep}
          setCurrentStep={
            setCurrentStep as React.Dispatch<React.SetStateAction<'select' | 'form' | 'preview' | 'upload'>>
          }
          allowMultipleSelection={false}
          previewContent={previewContent}
          setPreviewContent={setPreviewContent}
          onUploadComplete={() => {
            handleContentSwitchSubmit(
              courseId,
              stepOrder,
              user?.userId || '',
              values,
              existingStep,
              onSuccess,
              handleDismiss
            );
          }}
        />
      );
    }

    const renderContentCard = () => {
      return (
        <CurrentItemCard>
          <IconContainer>
            <i className={StepIcons[existingStep._type as keyof typeof StepIcons]} />
          </IconContainer>
          <ItemInfo>
            <h4>{existingStep.taskName || 'Untitled Step'}</h4>
            <p>{existingStep._type}</p>
          </ItemInfo>
          <ContentActionButton step={existingStep} onPreviewContent={handlePreviewContent} />
          <ActionButton onClick={handleSwitchContent} aria-label="Switch content">
            <i className="fas fa-exchange-alt" />
            Change
          </ActionButton>
        </CurrentItemCard>
      );
    };

    const baseForm = (
      <>
        {renderContentCard()}
        <FormField label="Name" name="name" type="text" placeholder="Enter step name" />
      </>
    );

    const descriptionFormField = (
      <StyledFormFieldContainer>
        <Label htmlFor="description">Description</Label>
        <RichTextEditor
          value={values.description || ''}
          onChange={newValue => setFieldValue('description', newValue)}
          allowedTags={['strong', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'a', 'img']}
          allowedAttributes={['href', 'target', 'rel']}
          placeholder="Enter step description"
        />
      </StyledFormFieldContainer>
    );

    if (selectedStepType === 'link' || selectedStepType === 'youtube' || selectedStepType === 'vimeo') {
      return (
        <>
          {baseForm}
          {descriptionFormField}
          <FormField label="URL" name="url" type="url" placeholder="Enter URL" />
          {(selectedStepType === 'youtube' || selectedStepType === 'vimeo') && <VideoPreview url={values.url || ''} />}
        </>
      );
    } else if (selectedStepType === 'assessment') {
      return baseForm;
    }

    return (
      <>
        {baseForm}
        {descriptionFormField}
      </>
    );
  };

  return (
    <Overlay dismissHandler={handleDismiss} disableBackgroundDismiss>
      <>
        <Overlay.CloseButton />

        <Formik
          initialValues={getInitialValues(selectedStepType as StepType, existingStep)}
          validationSchema={getValidationSchema(selectedStepType)}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setSubmitting, setFieldValue, handleSubmit: formikSubmit, values }) => (
            <EditStepContainer onSubmit={formikSubmit}>
              <OverlayContainer>
                <h3>Edit {existingStep._type || 'Step'}</h3>

                {renderFormFields(setFieldValue, values)}
                {error && <ErrorMessage>An error occurred while saving the step. Please try again.</ErrorMessage>}
              </OverlayContainer>
              <ModalFooter>
                {currentStep !== 'form' && (
                  <MediumButton type="button" onClick={handleBack}>
                    Back
                  </MediumButton>
                )}
                {currentStep === 'select' && (
                  <>
                    <MediumPrimaryButton
                      type="button"
                      onClick={() => {
                        setSubmitting(true);
                        handleContentSwitchSubmit(
                          courseId,
                          stepOrder,
                          user?.userId || '',
                          values,
                          existingStep,
                          onSuccess,
                          handleDismiss
                        ).then(() => {
                          setSubmitting(false);
                        });
                      }}
                      disabled={selectedContents.length === 0 || isSubmitting}
                      operating={isSubmitting}
                    >
                      Switch Content
                    </MediumPrimaryButton>
                  </>
                )}
                {currentStep === 'form' && (
                  <MediumPrimaryButton type="submit" disabled={isSubmitting} operating={isSubmitting}>
                    Save Changes
                  </MediumPrimaryButton>
                )}
              </ModalFooter>
            </EditStepContainer>
          )}
        </Formik>
      </>
    </Overlay>
  );
};
